import React from 'react'
import PropTypes from 'prop-types'
import i18n from 'i18next'
import {routeHelper} from '#config/routeKeys'
import HorizontalRule from '#components/a/HorizontalRule'
import NumberRow from '#components/m/MeineAusbildung/NumberRow'
import ProgressOverview from '#components/o/MeineAusbildung/ProgressOverview'
import {useAusbildungsplan} from '#hooks/useAusbildungsplan'
import Status from '#components/a/Status'
import {useNavigate} from 'react-router-dom'

export default function Ausbildungsplan(props) {
  const {userId, numbersDashboard, lastModifiedChapter, lastRejectedOrConfirmedChapters} = props
  const {getQuestionsContent, findDeep, getTitleForChapter, ready} = useAusbildungsplan(userId)
  const navigate = useNavigate()

  const showEmptyTile = !numbersDashboard || !lastRejectedOrConfirmedChapters || !ready

  if (showEmptyTile) {
    return <EmptyTile />
  }

  const totalChapters = calcTotalChapters(getQuestionsContent)
  const {confirmedItems} = numbersDashboard
  const percentFinished = Math.floor((confirmedItems / totalChapters) * 100)

  const openChapter = (userId, chapterId) => {
    navigate(routeHelper.ausbildungsplanQuestionsChapter(userId, chapterId))
  }

  const renderReviewedChapters = (getTitleForChapter, userId, chapters) => {
    return chapters.map((item, index) => (
      <button
        type="button"
        data-testid="ausbilder-edited-chapter"
        className="item"
        key={index.toString()}
        onClick={() => openChapter(userId, item.chapterId)}
      >
        <Status status={item.status} text={item.status} />
        {getTitleForChapter(item.chapterId)}
      </button>
    ))
  }

  return (
    <ProgressOverview
      data-testid="ausbildungsplandashboard-percentage"
      titleKey="menu_item_ausbildungsplan"
      percentage={percentFinished}
    >
      <div data-testid="ausbildungsplandashboard" className="ausbildungsplan-dashboard">
        <NumberRow numbers={sortNumbers(numbersDashboard)} />
        <HorizontalRule />
        <span className="title">{i18n.t('last_modified')}</span>

        <div className="in-progress-item">
          {lastModifiedChapter && findDeep(lastModifiedChapter.chapterId) ? (
            <button
              type="button"
              data-testid="in-progress"
              onClick={() => openChapter(userId, lastModifiedChapter.chapterId)}
            >
              {getTitleForChapter(lastModifiedChapter.chapterId)}
              <span className="question-number">
                {`${lastModifiedChapter.questionsDone}/${findTotalQuestionsByChapterId(
                  findDeep,
                  lastModifiedChapter.chapterId,
                )}`}
              </span>
            </button>
          ) : (
            <span>{i18n.t('ausbidlungsplan_no_in_progress_chapter')}</span>
          )}
        </div>

        <span className="title">{i18n.t('newest_reactions')}</span>
        <div className="reviewed-items">
          {lastRejectedOrConfirmedChapters.length > 0 ? (
            renderReviewedChapters(getTitleForChapter, userId, lastRejectedOrConfirmedChapters)
          ) : (
            <span>{i18n.t('ausbildungsplan_no_reviewed_chapter')}</span>
          )}
        </div>
      </div>
    </ProgressOverview>
  )
}

function calcTotalChapters(getQuestionsContent) {
  let totalChapters = 0
  getQuestionsContent().forEach(mainChapter => {
    totalChapters += mainChapter.content.length
  })
  return totalChapters
}

function findTotalQuestionsByChapterId(findDeep, chapterId) {
  const chapter = findDeep(chapterId)
  return findDeep(chapterId).content[0].content.length
}

function sortNumbers(numbers) {
  return [
    {color: 'green', value: numbers.confirmedItems, label: 'confirmed_items'},
    {color: 'blue', value: numbers.submittedItems, label: 'submitted_items'},
    {color: 'red', value: numbers.rejectedItems, label: 'rejected_items'},
    {color: 'grey', value: numbers.draftItems, label: 'draft_items'},
  ]
}

function EmptyTile() {
  return (
    <ProgressOverview titleKey="menu_item_ausbildungsplan" showProgressbar={false}>
      <div data-testid="ausbildungsplandashboardNoData" className="ausbildungsplan-dashboard">
        <span>{i18n.t('loading_dashboard_data_failed')}</span>
      </div>
    </ProgressOverview>
  )
}

Ausbildungsplan.propTypes = {
  userId: PropTypes.number.isRequired,
  numbersDashboard: PropTypes.shape({
    confirmedItems: PropTypes.number,
    submittedItems: PropTypes.number,
    rejectedItems: PropTypes.number,
    draftItems: PropTypes.number,
  }),
  lastModifiedChapter: PropTypes.shape({
    chapterId: PropTypes.string,
    questionsDone: PropTypes.number,
  }),
  lastRejectedOrConfirmedChapters: PropTypes.arrayOf(
    PropTypes.shape({
      chapterId: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
}
